import React, { useState } from 'react';
import {
  Card,
  Col,
  Flex,
  Modal,
  Row,
  Typography,
  Image,
  Grid,
  Button,
} from 'antd';
import {
  CheckOutlined,
  ScheduleOutlined,
  SafetyOutlined,
  LikeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { CDN_BASE_URL } from '../../../constants';
import { getCookie, setCookie } from '../../../utils/cookies';
import { t } from '../../../utils/i18n';
import { useGlobals } from '../../../globals-context';

const { useBreakpoint } = Grid;

export function PromoteDownPayments() {
  const screens = useBreakpoint();
  const globals = useGlobals();
  const navigate = useNavigate();
  const bannerVisibilityCounter = Number(
    getCookie('showPromoteDownPaymentsCounter')
  );
  const trackMethod = (globals as any)?.trackMethod || (() => {});
  const COUNTER_COOKIE_DURATION_DAYS = 90;

  const [isModalOpen, setIsModalOpen] = useState(true);

  const increaseShowDownPaymentsCounter = () => {
    const counter = getCookie('showPromoteDownPaymentsCounter');
    setCookie(
      'showPromoteDownPaymentsCounter',
      counter ? parseInt(counter, 10) + 1 : 1,
      COUNTER_COOKIE_DURATION_DAYS
    );
  };

  const handleOnActivateClick = () => {
    const { eventBus } = globals as any;
    setIsModalOpen(false);

    // Track the activation click
    trackMethod('promote_down_payments_activate_clicked', {
      source: 'banner',
      action: 'activate',
    });

    increaseShowDownPaymentsCounter();
    setCookie('showPromoteDownPayments', 'false', 3);

    setTimeout(() => {
      eventBus.emit('ui.action.promote_down_payments_activated', {
        source: 'banner',
      });
    }, 100);

    navigate('/application-settings/payments');
  };

  const handleMaybeLaterClick = () => {
    setIsModalOpen(false);

    // Track maybe later click
    trackMethod('promote_down_payments_maybe_later_clicked', {
      source: 'banner',
      action: 'maybe_later',
    });

    increaseShowDownPaymentsCounter();
    setCookie('showPromoteDownPayments', 'false', 7);
  };

  const handleStopShowingBannerClick = () => {
    setIsModalOpen(false);

    // Track stop showing banner click
    trackMethod('promote_down_payments_stop_showing_clicked', {
      source: 'banner',
      action: 'stop_showing',
    });
    increaseShowDownPaymentsCounter();
    setCookie('showPromoteDownPayments', 'false', 12);
  };

  const handleCancelOrCloseClick = () => {
    setIsModalOpen(false);

    // Track cancel/close click
    trackMethod('promote_down_payments_close_clicked', {
      source: 'banner',
      action: 'close',
    });

    increaseShowDownPaymentsCounter();
    setCookie('showPromoteDownPayments', 'false', 2);
  };

  // Track when the banner is shown
  React.useEffect(() => {
    if (isModalOpen) {
      trackMethod('promote_down_payments_banner_shown', {
        source: 'banner',
      });
    }
  }, [isModalOpen, trackMethod]);

  return (
    <Modal
      open={isModalOpen}
      centered
      title={null}
      footer={null}
      onCancel={handleCancelOrCloseClick}
      width={{
        xs: '90%',
        sm: '80%',
        md: '75%',
        lg: '75%',
        xl: '70%',
        xxl: '50%',
      }}
    >
      <Row justify="center" style={{ marginBottom: '1.6rem' }}>
        <Col span={24} style={{ textAlign: 'center' }}>
          <Typography.Title level={3}>
            {t('banners.promote-down-payments.title')}
          </Typography.Title>
        </Col>
      </Row>
      <Row
        justify="center"
        gutter={[16, 16]}
        style={{ marginBottom: '1.6rem' }}
      >
        <Col xs={24} md={8} style={{ display: 'flex' }}>
          <Card
            style={{
              flex: 1,
              justifyContent: 'center',
              color: 'white',
              background:
                'linear-gradient(141.5deg, #B37FEB 5.29%, #391085 77.85%)',
            }}
          >
            <Typography.Title
              level={5}
              style={{ color: 'white', margin: 0, marginBottom: '1.5rem' }}
            >
              {t('banners.promote-down-payments.security.title')}{' '}
              <SafetyOutlined style={{ color: 'white' }} />
            </Typography.Title>
            <Typography.Text style={{ color: 'white' }}>
              {t('banners.promote-down-payments.security.description')}
            </Typography.Text>
          </Card>
        </Col>
        <Col
          xs={24}
          md={8}
          style={{
            display: 'flex',
          }}
        >
          <Card
            style={{
              flex: 1,
              color: 'white',
              background:
                'linear-gradient(142.02deg, #5CDBD3 10.84%, #36CFC9 44.46%, #08979C 78.08%)',
            }}
          >
            <Typography.Title
              level={5}
              style={{ color: 'white', margin: 0, marginBottom: '1.5rem' }}
            >
              {t('banners.promote-down-payments.fair.title')}{' '}
              <LikeOutlined style={{ color: 'white' }} />
            </Typography.Title>
            <Typography.Text style={{ color: 'white' }}>
              {t('banners.promote-down-payments.fair.description')}
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} md={8} style={{ display: 'flex' }}>
          <Card
            style={{
              flex: 1,
              color: 'white',
              background:
                'linear-gradient(141.5deg, #69B1FF 11.89%, #003EB3 77.85%)',
            }}
          >
            <Typography.Title
              level={5}
              style={{ color: 'white', margin: 0, marginBottom: '1.5rem' }}
            >
              {t('banners.promote-down-payments.protection.title')}{' '}
              <ScheduleOutlined style={{ color: 'white' }} />
            </Typography.Title>
            <Typography.Text style={{ color: 'white' }}>
              {t('banners.promote-down-payments.protection.description')}
            </Typography.Text>
          </Card>
        </Col>
      </Row>
      <Row justify="center" style={{ marginBottom: '1.6rem' }}>
        <Col span={24} style={{ textAlign: 'center' }}>
          <Card
            styles={{
              body: { padding: 0, overflow: 'hidden' },
              header: { padding: 0, marginBottom: 16 },
            }}
            style={{ border: 'none' }}
          >
            <Flex justify="space-between" gap={16}>
              {screens.md && (
                <Flex style={{ width: '150%' }}>
                  <img
                    alt="Promote Down Payments Banner"
                    src={`${CDN_BASE_URL}/images/banner_spon_down_payments_bab.png`}
                    style={{ width: '100%' }}
                  />
                </Flex>
              )}
              <Flex
                vertical
                align="flex-start"
                justify="center"
                style={{
                  padding: 32,
                  background: '#E6F4FF',
                  textAlign: 'left',
                }}
              >
                <Typography.Title
                  level={1}
                  style={{
                    margin: 0,
                    fontSize: '96px',
                    lineHeight: 0,
                    color: '#1677FF',
                  }}
                >
                  “
                </Typography.Title>
                <Typography.Title
                  level={4}
                  style={{ margin: 0, marginBottom: '2rem' }}
                >
                  {t('banners.promote-down-payments.testimony.text')}”
                </Typography.Title>
                <Typography.Text>
                  {t('banners.promote-down-payments.testimony.author')}
                </Typography.Text>
              </Flex>
            </Flex>
          </Card>
        </Col>
      </Row>
      <Row
        justify="center"
        style={{
          marginBottom: '1.6rem',
          border: '1px solid #eee',
          padding: '1rem',
        }}
      >
        <Col xs={24} md={12} style={{ textAlign: 'center' }}>
          <Flex
            gap={10}
            align="center"
            justify="center"
            style={{ width: '100%' }}
          >
            <CheckOutlined style={{ color: '#00d0be' }} />
            <Typography.Text>
              {t('banners.promote-down-payments.benefits.change-anytime')}
            </Typography.Text>
          </Flex>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: 'center' }}>
          <Flex
            gap={10}
            align="center"
            justify="center"
            style={{ width: '100%' }}
          >
            <CheckOutlined style={{ color: '#00d0be' }} />
            <Typography.Text>
              {t('banners.promote-down-payments.benefits.low-fee')}
            </Typography.Text>
          </Flex>
        </Col>
      </Row>
      <Row
        gutter={[16, 24]}
        justify="center"
        style={{ marginBottom: '1.6rem' }}
      >
        <Col span={24} style={{ textAlign: 'center' }}>
          <Button
            type="primary"
            size="large"
            block
            onClick={handleOnActivateClick}
          >
            {t('banners.promote-down-payments.buttons.activate')}
          </Button>
        </Col>
        {bannerVisibilityCounter < 10 && (
          <Col span={24} style={{ textAlign: 'center' }}>
            <Button
              color="default"
              variant="link"
              onClick={handleMaybeLaterClick}
            >
              {t('banners.promote-pay-online.buttons.maybe-later')}
            </Button>
          </Col>
        )}
        {bannerVisibilityCounter >= 10 && (
          <Col span={24} style={{ textAlign: 'center' }}>
            <Button
              color="default"
              variant="link"
              onClick={handleStopShowingBannerClick}
            >
              {t('banners.promote-down-payments.buttons.stop-showing')}
            </Button>
          </Col>
        )}
      </Row>
    </Modal>
  );
}
